import type { Selection, TypeFromSelection } from 'groqd'
import { q } from 'groqd'
import type { ZodLiteral, ZodObject, ZodOptional, ZodString } from 'zod'
import * as z from 'zod'

import { SHAPES } from '@nxt-insurance/ui-kit/components/SvgIcon/shapes'
import type { IconBgShape } from '@nxt-insurance/ui-kit/components/SvgIcon/types'
import { BORDER_RADIUS, COLOR, SPACE } from '@nxt-insurance/ui-kit/constants'
import type { BorderRadius, Color, Space } from '@nxt-insurance/ui-kit/types'

import { locales } from '@/constants'
import { getLocaleParts } from '@/utils/locale'

export const getSlugsQuery = () => {
  const slugs: Record<string, ZodOptional<ZodObject<{ current: ZodString }>>> = {}
  locales.forEach((locale) => {
    const [lang] = getLocaleParts(locale)
    slugs[lang] = q
      .object({
        current: q.string(),
      })
      .optional()
  })
  return slugs
}

const routeTarget = (name: string) =>
  q(name)
    .deref()
    .grab({
      _type: q.string(),
      market: q.string(),
      slug: ['slug[$lang].current', q.string()],
      folder: ['folder[$lang]', q.string().nullable()],
      redirectActive: q.boolean().nullable(),
      redirectTarget: q.string().nullable(),
    })

export const imageAsset = {
  url: q.string(),
  _id: q.string(),
  extension: q.string(),
  size: q.number(),
  uploadId: q.string().nullable(),
  metadata: q.object({
    lqip: q.string(),
    dimensions: q.object({
      aspectRatio: q.number(),
      height: q.number(),
      width: q.number(),
    }),
  }),
} satisfies Selection

const imageType = {
  _type: q.literal('image'),
  asset: q.object(imageAsset),
} satisfies Selection

const illustrationEntry = (name: string) =>
  q(name)
    .filter()
    .grab({
      _type: q.string(),
      image: q('image').grab({
        _type: q.literal('image'),
        asset: q('asset').deref().grab(imageAsset),
      }),
      language: q.string(),
      market: q.string(),
    })
    .nullable()

export const flexibleImage = {
  _type: q.literal('flexibleImage'),
  alt: ['alt[$lang]', q.string().nullable()],
  imageWidth: q.number().nullable(),
  url: q.string().nullable(),
  desktopImage: q(`desktopImage[] {
    _type == 'image' => {
      _type,
      asset->
    },
    _type == 'reference' => @->,
    _type == 'illustration' => {
      ...
    }
  }`)
    .filter()
    .select({
      '_type == "image"': imageType,
      default: {
        _type: q.literal('illustration'),
        imageFiles: illustrationEntry('imageFiles'),
      },
    })
    .nullable(),
} satisfies Selection

export const campaign = {
  _type: q.literal('campaign'),
  name: ['name.current', q.string()],
  redirectionTarget: q('redirectionTarget')
    .grab({
      anchor: q.string().nullable(),
      route: routeTarget('route'),
    })
    .nullable(),
  discountInfo: ['discountInfo[$lang]', q.string()],
  popup: q('popup')
    .grab({
      buttonLabel: ['buttonLabel[$lang]', q.string().nullable()],
      text: ['text[$lang]', q.string()],
      title: ['title[$lang]', q.string()],
    })
    .nullable(),
} satisfies Selection

const spaceArr = (Object.keys(SPACE) as Space[]).map((s) => q.literal(s))

export const spaceEnum = z.enum(Object.keys(SPACE) as [Space, ...Space[]])

export const spaceUnion = q.union(
  spaceArr as [ZodLiteral<Space>, ZodLiteral<Space>, ...ZodLiteral<Space>[]]
)

const colorArr = (Object.keys(COLOR) as Color[]).map((s) => q.literal(s)) as [
  ZodLiteral<Color>,
  ZodLiteral<Color>,
  ...ZodLiteral<Color>[],
]

export const colorEnum = z.enum(Object.keys(COLOR) as [Color, ...Color[]])

export const colorUnion = q.union(colorArr)

const borderRadiusArr = (Object.keys(BORDER_RADIUS) as BorderRadius[]).map((s) => q.literal(s)) as [
  ZodLiteral<BorderRadius>,
  ZodLiteral<BorderRadius>,
  ...ZodLiteral<BorderRadius>[],
]

export const borderRadiusEnum = z.enum(
  Object.keys(BORDER_RADIUS) as [BorderRadius, ...BorderRadius[]]
)

export const borderRadiusUnion = q.union(borderRadiusArr)

const iconShapesEnum = z.enum(Object.keys(SHAPES) as [IconBgShape, ...IconBgShape[]])

export const codedSectionUnion = q.union([
  q.literal('blogOverview.posts'),
  q.literal('press.archive'),
  q.literal('productMenu'),
])

export const horizontalAlignmentEnum = z.enum(['left', 'center', 'right'])

export const horizontalAlignmentUnion = q.union([
  q.literal('left'),
  q.literal('center'),
  q.literal('right'),
])

export type HorizontalAlignment = z.infer<typeof horizontalAlignmentUnion>

export const verticalAlignmentUnion = q.union([
  q.literal('top'),
  q.literal('center'),
  q.literal('bottom'),
])

export const iconSchema = z.object({
  name: z.string().optional(),
  iconAsset: z
    .object({
      _type: z.literal('reference'),
      _ref: z.string(),
    })
    .optional(),
  bgColor: colorEnum.optional(),
  bgShape: iconShapesEnum.optional(),
  alt: z.string().optional(),
})

export const dimension = z.object({
  size: z.number(),
  scale: z.enum(['px', '%']),
})

export type ImageType = TypeFromSelection<typeof imageType>
export type IconType = z.infer<typeof iconSchema>
